// Hero illustration is served from /public/assets/login/ rather
// than imported — keeps the 2.7 MB SVG out of the JS bundle and
// in the browser cache as a static asset.
const signUpImg = `${import.meta.env.BASE_URL}assets/login/signUpImg.svg`;
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faUserCircle,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { useT, useI18n } from "../../i18n/I18nContext";
import LocalePicker from "../../i18n/LocalePicker";
import { useAuth } from "../../auth/AuthContext";
import { logger } from "../../util/logger";
import { checkText, filterErrorMessage } from "../../util/contentFilter";
// Brand wordmark — same transparent-PNG used in navbar / footer /
// login. Mobile-only here (md:hidden) and sized smaller than on
// /login since the signup form has 5 fields + a terms checkbox
// to fit above the fold.
import logo from "../../assets/plumescript-logo-removebg.png";

const MIN_PASSWORD_LENGTH = 8;

const SignUp = () => {
  const t = useT();
  const { locale } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullname, setFullname] = useState("");
  const [username, setUsername] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const toggleTerms = (checked) => {
    setAcceptedTerms(checked);
    if (checked) setTermsError(false);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { user, status, signUp } = useAuth();

  const nextParam = new URLSearchParams(location.search).get("next");
  const nextTarget =
    nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")
      ? nextParam
      : "/";

  const register = async (event) => {
    if (event) event.preventDefault();
    if (submitting) return;
    const trimmedFullname = fullname.trim();
    const trimmedUsername = username.trim();
    const trimmedEmail = email.trim();
    if (!trimmedFullname || !trimmedUsername || !trimmedEmail || !password) {
      toast.error(t("auth.fill_all_fields"));
      return;
    }
    // Pre-flight content filter on the identity fields. Backend
    // re-checks; this is for instant feedback so the user doesn't
    // wait for a round-trip to learn their handle is reserved.
    const usernameVerdict = checkText(trimmedUsername, { kind: "username" });
    if (!usernameVerdict.ok) {
      toast.error(filterErrorMessage(usernameVerdict, t));
      return;
    }
    const fullnameVerdict = checkText(trimmedFullname, {
      kind: "display",
      minLength: 1,
      maxLength: 80,
    });
    if (!fullnameVerdict.ok) {
      toast.error(filterErrorMessage(fullnameVerdict, t));
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast.error(
        t("auth.weak_password", { min: String(MIN_PASSWORD_LENGTH) }),
      );
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t("auth.passwords_dont_match"));
      return;
    }
    if (!acceptedTerms) {
      setTermsError(true);
      toast.error(t("auth.terms_required"));
      return;
    }
    setTermsError(false);
    setSubmitting(true);
    try {
      await signUp({
        email: trimmedEmail,
        password,
        username: trimmedUsername,
        fullname: trimmedFullname,
        // Stamp the active i18n locale on the new account so the
        // welcome email + every future transactional mail goes out
        // in the language the user was just browsing in.
        locale,
      });
    } catch (err) {
      logger.warn("register_failed", err?.response?.data?.error ?? err?.message);
      const code = err?.response?.data?.error;
      toast.error(
        code === "user_already_exists"
          ? t("auth.user_already_exists")
          : code === "weak_password"
            ? t("auth.weak_password", { min: String(MIN_PASSWORD_LENGTH) })
            : err?.message ?? t("auth.signup_failed"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (status === "signedIn" && user) navigate(nextTarget, { replace: true });
  }, [status, user, navigate, nextTarget]);

  // Shared input chrome — pill-rounded to match the form pattern
  // on /setting and /contact so the auth flow reads as the same
  // component vocabulary the rest of the app uses. ``pr-12`` on
  // every field reserves space for the trailing icon (text inputs)
  // or the show/hide-password toggle (password inputs) that sits
  // absolute-right inside the same input chrome.
  const inputClass =
    "w-full px-4 py-3 pr-12 rounded-full border border-border bg-surface lg:bg-surfaceAlt text-text text-[16px]/[24px] placeholder:text-secondText/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition";

  return (
    <div className="relative min-h-screen flex flex-col lg:flex-row bg-surfaceAlt lg:bg-bg">
      <LocalePicker className="absolute top-3 right-4 z-20" />

      {/* Hero illustration — desktop only. Same trade-off as
          /login: collapsing on mobile saves a 2.7 MB asset and
          puts the form above the fold. */}
      <div className="hidden lg:block lg:w-2/3">
        {/* Decorative hero doubles as a "back to landing" hit; the
            brand link below provides the keyboard equivalent. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <img
          src={signUpImg}
          alt=""
          onClick={() => navigate("/")}
          className="cursor-pointer object-cover w-full h-screen"
        />
      </div>

      {/* Form column. Mobile-first padding (px-5), max-w-sm so the
          form doesn't sprawl when the viewport is wider than the
          1/3 column it occupies on lg. ``lg:bg-surface`` keeps the
          desktop look (form column reads as the right third);
          mobile lets the parent's bg-surfaceAlt show through so
          the card below sits on a different shade. */}
      <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-10 lg:py-12 lg:bg-surface">
        <div className="w-full max-w-sm">
          {/* Mobile-only brand mark — sized smaller than /login's
              (h-12 vs h-20) because the signup form has 5 fields +
              a terms checkbox to fit above the fold. */}
          <Link
            to="/"
            aria-label="Plumescript"
            className="lg:hidden flex justify-center mb-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md"
          >
            <img
              src={logo}
              alt=""
              aria-hidden="true"
              className="app-logo h-12 w-auto"
            />
          </Link>

          {/* Form card — same chrome as Login (rounded-2xl border
              shadow). Tighter padding (p-5) than Login's p-6 to
              keep the 5-input form + terms checkbox above the
              fold on common phone sizes. Reverts to bare on lg
              where the form fills its own column. */}
          <div className="bg-surface border border-border rounded-2xl shadow-soft p-5 sm:p-6 lg:bg-transparent lg:border-0 lg:shadow-none lg:p-0">
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl lg:text-2xl font-bold text-text">
                {t("auth.signup.title")}
              </h1>
              <p className="mt-2 text-sm text-secondText">
                {t("auth.signup.subtitle")}
              </p>
            </div>

          <form onSubmit={register} noValidate className="space-y-3.5">
            <div>
              <label htmlFor="fname" className="sr-only">
                {t("auth.fullname_label")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="fname"
                  name="fname"
                  value={fullname}
                  onChange={(e) => setFullname(e.target.value)}
                  className={inputClass}
                  placeholder={t("auth.fullname_label")}
                  autoComplete="name"
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- focus is intentional on this modal/form's primary input
                  autoFocus
                  required
                />
                <span className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-secondText">
                  <FontAwesomeIcon icon={faUser} />
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="sr-only">
                {t("auth.email_label")}
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder={t("auth.email_label")}
                  autoComplete="email"
                  required
                />
                <span className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-secondText">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="uname" className="sr-only">
                {t("auth.username_label")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="uname"
                  name="uname"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={inputClass}
                  placeholder={t("auth.username_label")}
                  autoComplete="username"
                  required
                />
                <span className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-secondText">
                  <FontAwesomeIcon icon={faUserCircle} />
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="sr-only">
                {t("auth.create_password_label")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  placeholder={t("auth.create_password_label")}
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={
                    showPassword
                      ? t("auth.hide_password")
                      : t("auth.show_password")
                  }
                  className="absolute inset-y-0 right-0 flex items-center px-4 text-secondText hover:text-text transition"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="password_confirm" className="sr-only">
                {t("auth.confirm_password_label")}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="password_confirm"
                  name="password_confirm"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  placeholder={t("auth.confirm_password_label")}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={
                    showConfirmPassword
                      ? t("auth.hide_password")
                      : t("auth.show_password")
                  }
                  className="absolute inset-y-0 right-0 flex items-center px-4 text-secondText hover:text-text transition"
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div className="pt-1">
              <label htmlFor="accept_terms" className="flex items-start gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  id="accept_terms"
                  name="accept_terms"
                  className={`w-4 h-4 mt-0.5 accent-primary shrink-0 ${
                    termsError ? "ring-2 ring-danger rounded" : ""
                  }`}
                  checked={acceptedTerms}
                  onChange={(e) => toggleTerms(e.target.checked)}
                  aria-invalid={termsError || undefined}
                  aria-describedby={termsError ? "accept_terms_error" : undefined}
                  required
                />
                <span className="leading-snug">
                  {t("auth.terms_prefix")}{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {t("auth.terms")}
                  </Link>
                  .
                </span>
              </label>
              {termsError && (
                <p
                  id="accept_terms_error"
                  role="alert"
                  className="text-danger text-xs mt-1.5 ml-6"
                >
                  {t("auth.terms_required")}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="app-button-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? t("common.loading") : t("auth.signup_cta")}
            </button>
          </form>

            <p className="mt-6 text-center text-sm text-secondText">
              {t("auth.have_account")}{" "}
              <Link to="/login" className="text-primary font-medium hover:underline">
                {t("auth.log_in")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
